import { Label } from '@components/ui/label';
import { cn } from '@utils/cn';
import { FC, ReactNode } from 'react';

type FieldProps = {
  label: string;
  htmlFor: string;
  description?: string;
  /** Short qualifier rendered above the description, e.g. "Optional". */
  hint?: string;
  error?: string;
  required?: boolean;
  /**
   * `stacked` puts the control under the label (the default everywhere).
   * `split` puts label and description on the left, control on the right —
   * used by the long settings-style forms.
   */
  layout?: 'stacked' | 'split';
  className?: string;
  children: ReactNode;
};

const Field: FC<FieldProps> = ({
  label,
  htmlFor,
  description,
  hint,
  error,
  required,
  layout = 'stacked',
  className,
  children
}) => {
  const meta = (
    <div className="min-w-0 space-y-1">
      <Label htmlFor={htmlFor} className="text-label">
        {label}
        {required && (
          <span aria-hidden className="ml-0.5 text-muted-foreground">
            *
          </span>
        )}
      </Label>
      {hint && <p className="text-meta text-muted-foreground">{hint}</p>}
      {description && (
        <p className="max-w-sm text-caption text-muted-foreground">
          {description}
        </p>
      )}
    </div>
  );

  const control = (
    <>
      {children}
      {error && (
        <p className="text-meta text-destructive" role="alert">
          {error}
        </p>
      )}
    </>
  );

  if (layout === 'split') {
    return (
      <div
        className={cn(
          'flex flex-wrap justify-between gap-4 border-b border-border py-6 last:border-b-0',
          className
        )}
      >
        {meta}
        <div className="w-full space-y-1.5 md:w-1/2">{control}</div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      {meta}
      {control}
    </div>
  );
};

export default Field;
export { Field };
